import {
	Component,
	OnInit,
	ViewEncapsulation,
	OnDestroy,
	Output,
	EventEmitter,
	Input,
	OnChanges,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { FormControl } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { DatacenterViewService } from '../../services/datacenter-view.service';
import { pageTransitions } from '../../animations/page-transitions';
import {
	ColumnDefAndHeader,
	DatacenterType,
} from '../../models/datacenter-aggrid-view';
import { DatacenterDefaultConfiguration } from '../datacenter-default-configuration';
import { safe } from '../../utils/safe';
import { SlideButtonComponent } from '../../components/slide-button/slide-button.component';

import { ColumnAggregator } from './column-aggregator';

@Component({
	selector: 'copo-datacenter-sorting',
	templateUrl: './datacenter-sorting.component.html',
	styleUrl: './datacenter-sorting.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [TranslateModule, NgFor, NgIf, MatIconModule, SlideButtonComponent, CdkDropList, CdkDrag]
})
export class DatacenterSortingComponent implements OnInit, OnChanges, OnDestroy {
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	@Input() currentColumnStateForSorting?: ColumnAggregator;
	@Input() togglePosition = true;
	searchTextControl = new FormControl('');
	columnAggregatorList: ColumnAggregator = new ColumnAggregator([]);
	allColumns: ColumnDefAndHeader[] = [];
	sortList: ColumnDefAndHeader[] = [];
	sortMap = new Map<ColumnDefAndHeader, 'asc' | 'desc'>();
	sortStringList: string[] = [];
	private searchTextSubscription?: Subscription;

	constructor(
		readonly viewService: DatacenterViewService,
		private readonly defaultConf: DatacenterDefaultConfiguration,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
	) {
		const { viewToEdit } = this.viewService;
		if (!viewToEdit) {
			throw new Error('no view to edit');
		}
		const datacenterType =
			<DatacenterType | undefined> this.route.snapshot.queryParams['type'] ?? viewToEdit.viewType;
		this.allColumns = this.defaultConf.getAllColumnsAndHeader(datacenterType).sort(
			(c1, c2) => c1.header.localeCompare(c2.header)
		);
		const columns = viewToEdit.currentView.columnState.map(value => ({
			colId: value.colId,
			header: this.defaultConf.getHeaderForColumn(datacenterType, value.colId),
			pinned: value.pinned,
			sortable: this.defaultConf.getIsColumnSortable(datacenterType, value.colId)
		}));

		const sortModel = viewToEdit.currentView.sortModel || [];

		this.columnAggregatorList = new ColumnAggregator(columns.length > 0
			? [...this.allColumns] : []);
		for (const sortColumn of sortModel) {
			this.columnAggregatorList.addSortColumn(sortColumn);
		}
		for (const column of columns) {
			this.columnAggregatorList.addVisibleColumn(column);
		}
		this.sortMap = this.columnAggregatorList.sortColumnMap;
		this.sortList = this.columnAggregatorList.sortColumnList;
		for (const sortItem of this.sortList) {
			this.sortStringList.push(sortItem.colId);
		}
	}

	ngOnChanges() {
		if (this.currentColumnStateForSorting) {
			this.columnAggregatorList = this.currentColumnStateForSorting;
		}
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined> value;
			if (searchText === undefined) {
				return;
			}

			const allHiddenColumns = this.allColumns.filter(column => {
				const index = this.columnAggregatorList.getColumnsToShow().findIndex(col => col.colId === column.colId);
				return index === -1;
			});
			this.columnAggregatorList.allColumns = allHiddenColumns.filter(column => {
				if (column.header) {
					return column.header.search(searchText) >= 0;
				}
				return false;
			});
		});
	}

	onToggleChange() {
		this.typeOfTab.emit('layout');
		this.togglePositionChange.emit(false);
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	addToSort(item: ColumnDefAndHeader) {
		if (!item.sortable) {
			return;
		}
		const index = this.sortStringList.indexOf(item.colId, 0);
		if (index === -1) {
			if (this.sortList.length < 5) {
				this.sortStringList.push(item.colId);
				this.sortList.push(item);
				this.sortMap.set(item, 'desc');
			}
		} else {
			this.sortList.splice(index, 1);
			this.sortStringList.splice(index, 1);
			for (const key of this.sortMap.keys()) {
				if (key.colId !== item.colId) {
					return;
				}
				this.sortMap.delete(key);
			}
		}
	}

	setSortingOrder(item: ColumnDefAndHeader) {
		if (this.sortMap.get(item) === 'desc') {
			this.sortMap.set(item, 'asc');
		} else if (this.sortMap.get(item) === 'asc') {
			this.sortMap.set(item, 'desc');
		}
	}

	checkSortingOrder(item: ColumnDefAndHeader) {
		return this.sortMap.get(item);
	}

	isAdded(item: ColumnDefAndHeader) {
		const index = this.sortStringList.indexOf(item.colId, 0);
		return index !== -1;
	}

	drop(event: CdkDragDrop<string[]>) {
		moveItemInArray(this.sortList, event.previousIndex, event.currentIndex);
		moveItemInArray(this.sortStringList, event.previousIndex, event.currentIndex);
	}

	getSortModel() {
		if (this.sortList.length === 0) {
			return null;
		}
		return this.sortList.map(value => ({ colId: value.colId, sort: safe(this.sortMap.get(value)) }));
	}

	checkIfItemsWasMoved(columnsToCheck?: ColumnDefAndHeader[]) {
		if (!columnsToCheck) {
			return;
		}
		for (const row of columnsToCheck) {
			for (const sortItem of this.sortList) {
				if (row.colId !== sortItem.colId) {
					continue;
				}
				this.sortStringList.splice(this.sortStringList.indexOf(sortItem.colId, 0), 1);
				this.sortList.splice(this.sortList.indexOf(sortItem, 0), 1);
				for (const key of this.sortMap.keys()) {
					if (key.colId !== sortItem.colId) {
						return;
					}
					this.sortMap.delete(key);
				}
			}
		}
	}

	async close() {
		await this.router.navigate(['../'], { relativeTo: this.route });
	}
}
